import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import VM from 'openblock-vm';
import {connect} from 'react-redux';
import {compose} from 'redux';
import {defineMessages, injectIntl, intlShape} from 'react-intl';

import analytics from '../lib/analytics';
import {setDeviceData} from '../reducers/device-data';
import {getConfig} from '../lib/scratch-config.js';

import {makeDeviceLibrary} from '../lib/libraries/devices/index.jsx';

import LibraryComponent from '../components/library/library.jsx';
import deviceIcon from '../components/action-menu/icon--sprite.svg';
import styles from './device-library.css';
import windowsIcon from './icon--windows.svg';
import macIcon from './icon--mac.svg';

// How long a single probe waits before calling the helper "not running".
const DETECT_TIMEOUT = 2000;
// How often to probe again while the prompt is on screen. The user may start
// KidsBlock Link while looking at it, so the prompt has to notice on its own.
const DETECT_INTERVAL = 2500;

/**
 * Detect the operating system, to highlight the right download.
 * @returns {'windows'|'mac'|'other'} the platform the browser reports
 */
const detectOS = () => {
    const ua = navigator.userAgent.toLowerCase();
    if (ua.includes('win')) return 'windows';
    if (ua.includes('mac')) return 'mac';
    return 'other';
};

const messages = defineMessages({
    deviceTitle: {
        defaultMessage: 'Choose an Device',
        description: 'Heading for the device library',
        id: 'gui.deviceLibrary.chooseADevice'
    },
    deviceUrl: {
        defaultMessage: 'Enter the URL of the device',
        description: 'Prompt for unoffical device url',
        id: 'gui.deviceLibrary.deviceUrl'
    },
    arduinoTag: {
        defaultMessage: 'Arduino',
        description: 'Arduino tag to filter all arduino devices.',
        id: 'gui.deviceLibrary.arduinoTag'
    },
    microPythonTag: {
        defaultMessage: 'MicroPython',
        description: 'Micro python tag to filter all micro python devices.',
        id: 'gui.deviceLibrary.microPythonTag'
    },
    robotTag: {
        defaultMessage: 'Robot',
        description: 'robot tag to filter all robot devices.',
        id: 'gui.deviceLibrary.robotTag'
    },
    kitTag: {
        defaultMessage: 'Kit',
        description: 'Kit tag to filter all kit devices.',
        id: 'gui.deviceLibrary.kitTag'
    },
    kidsbitsTag: {
        defaultMessage: 'Kidsbits',
        description: 'Kidsbits tag to filter all kidsbits devices.',
        id: 'gui.deviceLibrary.kidsbitsTag'
    },
    AITag: {
        defaultMessage: 'AI',
        description: 'AI tag to filter all AI devices.',
        id: 'gui.deviceLibrary.AITag'
    },
    // KidsBlock Link 弹窗文字（默认值由 src/lib/kidsblock-link-messages.js 提供译文）
    linkModalTitle: {
        defaultMessage: 'KidsBlock Link is required',
        description: 'Title of the KidsBlock Link installation modal',
        id: 'gui.kidsblockLink.modalTitle'
    },
    linkModalIntro: {
        defaultMessage: 'KidsBlock online reaches your board through KidsBlock Link. ' +
            'Please keep it running.',
        description: 'Explains why the local helper is needed',
        id: 'gui.kidsblockLink.modalIntro'
    },
    linkStep1Title: {
        defaultMessage: 'First time here? Download and install KidsBlock Link for your system.',
        description: 'Step 1 title in the KidsBlock Link modal',
        id: 'gui.kidsblockLink.step1Title'
    },
    linkStep2Title: {
        defaultMessage: 'Keep KidsBlock Link running in the background to connect and upload code to your board.',
        description: 'Step 2 title in the KidsBlock Link modal',
        id: 'gui.kidsblockLink.step2Title'
    },
    linkDownloadWindows: {
        defaultMessage: 'Download',
        description: 'Download button label for Windows',
        id: 'gui.kidsblockLink.downloadWindows'
    },
    linkDownloadWindowsActive: {
        defaultMessage: 'Click to Download',
        description: 'Active download button label for Windows',
        id: 'gui.kidsblockLink.downloadWindowsActive'
    },
    linkDownloadMac: {
        defaultMessage: 'Download',
        description: 'Download button label for Mac',
        id: 'gui.kidsblockLink.downloadMac'
    },
    linkDownloadMacActive: {
        defaultMessage: 'Click to Download',
        description: 'Active download button label for Mac',
        id: 'gui.kidsblockLink.downloadMacActive'
    },
    linkDetecting: {
        defaultMessage: 'Looking for KidsBlock Link on this computer...',
        description: 'Shown while the editor probes the local helper',
        id: 'gui.kidsblockLink.detecting'
    },
    linkInstalledQuestion: {
        defaultMessage: 'Already installed KidsBlock Link?',
        description: 'Heading of the step that opens an already installed helper',
        id: 'gui.kidsblockLink.installedQuestion'
    },
    linkBlockedByBrowser: {
        defaultMessage: 'This browser is blocking pages from reaching services on your computer ' +
            '(local network access is denied). Allow it - look for the permission icon in the ' +
            'address bar - or use another browser.',
        description: 'Shown when the browser refuses the loopback connection outright',
        id: 'gui.kidsblockLink.blockedByBrowser'
    },
    linkLaunchAttempted: {
        defaultMessage: 'Asked the system to start it. Nothing happened? Open it yourself from the ' +
            'Start menu (Windows) or Launchpad (macOS).',
        description: 'Shown after the editor tried to start the helper through the OS',
        id: 'gui.kidsblockLink.launchAttempted'
    },
    linkDetected: {
        defaultMessage: 'KidsBlock Link is running. Connecting...',
        description: 'Shown when the local helper answers',
        id: 'gui.kidsblockLink.detected'
    },
    linkOpenInstalled: {
        defaultMessage: 'Open the installed KidsBlock Link',
        description: 'Button that asks the system to start the helper',
        id: 'gui.kidsblockLink.openInstalled'
    },
    linkLaunchHint: {
        defaultMessage: 'Nothing happened? Open KidsBlock Link yourself from the Start menu ' +
            '(Windows) or Launchpad (macOS).',
        description: 'Hint under the button that starts the local helper',
        id: 'gui.kidsblockLink.launchHint'
    }
});

const ARDUINO_TAG = {tag: 'Arduino', intlLabel: messages.arduinoTag};
const MICROPYTHON_TAG = {tag: 'MicroPython', intlLabel: messages.microPythonTag};
const ROBOT_TAG = {tag: 'Robot', intlLabel: messages.robotTag};
const KIT_TAG = {tag: 'Kit', intlLabel: messages.kitTag};
const KIDSBITS_TAG = {tag: 'Kidsbits', intlLabel: messages.kidsbitsTag};
const AI_TAG = {tag: 'AI', intlLabel: messages.AITag};
const tagListPrefix = [ARDUINO_TAG, MICROPYTHON_TAG, ROBOT_TAG, KIT_TAG, KIDSBITS_TAG, AI_TAG];

class DeviceLibrary extends React.PureComponent {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleItemSelect',
            'handleBannerClose',
            'detectKidsblocklinkInstalled',
            'handleOpenInstalledLink'
        ]);
        this.state = {
            // null = 检测中, true = 已运行, false = 未检测到
            linkInstalled: null,
            bannerDismissed: false,
            launchAttempted: false,
            // 探测失败也可能是浏览器把 ws://127.0.0.1 拦了（Chrome 的本地网络访问限制），
            // 那是权限问题，不是"没装"，提示必须区分开，否则会把用户引到错误的方向。
            localNetworkBlocked: false
        };
        this.detectTimer = null;
        this.pollTimer = null;
        this.launchedAt = 0;
    }
    componentDidMount () {
        this.props.vm.extensionManager.getDeviceList().then(data => {
            if (data) {
                this.props.onSetDeviceData(makeDeviceLibrary(data));
            }
        });
        // 每次打开设备选择页面时检测 KidsBlock Link
        this.detectKidsblocklinkInstalled();
    }
    componentWillUnmount () {
        this.stopPolling();
        if (this.detectTimer) {
            clearTimeout(this.detectTimer);
            this.detectTimer = null;
        }
    }

    /**
     * KidsBlock Link 的地址与协议由宿主配置，默认值见 scratch-config.js。
     * @returns {object} wsUrl / scheme / downloadUrls
     */
    linkSettings () {
        const configured = getConfig().kidsblockLink || {};
        return {
            wsUrl: configured.wsUrl || 'ws://127.0.0.1:20111',
            scheme: configured.scheme || 'kidsblocklink://',
            downloadUrls: configured.downloadUrls || {}
        };
    }

    /**
     * 浏览器是否明确拒绝了本地网络访问。Chrome 会对 https 页面连 ws://127.0.0.1
     * 做这个检查；被拒时连接会以 ERR_BLOCKED_BY_LOCAL_NETWORK_ACCESS_CHECKS 失败，
     * 和"对端没在跑"表现一样，只能靠权限状态区分。
     * @returns {Promise<boolean>} 被拒绝时为 true
     */
    isLocalNetworkBlocked () {
        if (!navigator.permissions || !navigator.permissions.query) {
            return Promise.resolve(false);
        }
        try {
            return navigator.permissions.query({name: 'local-network-access'})
                .then(status => status.state === 'denied')
                .catch(() => false);
        } catch (e) {
            return Promise.resolve(false);
        }
    }

    startPolling () {
        if (this.pollTimer) return;
        // The user may install or start the helper while this prompt is open, so
        // keep asking instead of making them reopen the device list.
        this.pollTimer = setInterval(() => {
            if (this.state.linkInstalled !== false || this.state.bannerDismissed) {
                this.stopPolling();
                return;
            }
            this.detectKidsblocklinkInstalled();
        }, DETECT_INTERVAL);
    }

    stopPolling () {
        if (this.pollTimer) {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
        }
    }

    /**
     * 通过 WebSocket 检测 KidsBlock Link 本地服务是否运行。
     */
    detectKidsblocklinkInstalled () {
        if (this.detectTimer) {
            clearTimeout(this.detectTimer);
            this.detectTimer = null;
        }
        let settled = false;
        let ws;
        const finish = running => {
            if (settled) return;
            settled = true;
            if (this.detectTimer) {
                clearTimeout(this.detectTimer);
                this.detectTimer = null;
            }
            if (ws) {
                try {
                    ws.close();
                } catch (e) {
                    // Already closed, or never opened.
                }
            }
            if (running) {
                this.stopPolling();
                this.setState({linkInstalled: true, localNetworkBlocked: false});
                return;
            }
            this.isLocalNetworkBlocked().then(blocked => {
                this.setState({linkInstalled: false, localNetworkBlocked: blocked});
                if (!this.state.bannerDismissed) {
                    this.startPolling();
                }
            });
        };
        this.detectTimer = setTimeout(() => {
            this.detectTimer = null;
            finish(false);
        }, DETECT_TIMEOUT);
        try {
            ws = new WebSocket(this.linkSettings().wsUrl);
            ws.onopen = () => finish(true);
            ws.onerror = () => finish(false);
            ws.onclose = () => finish(false);
        } catch (e) {
            finish(false);
        }
    }

    /**
     * 尝试启动电脑上已安装的 KidsBlock Link。
     *
     * 浏览器看不到本机装了什么程序，唯一能"打开"它的办法是把一个自定义
     * URL 协议交给系统（由 KidsBlock Link 的安装程序注册）。用隐藏 iframe
     * 触发，这样即使协议没有注册，编辑器页面也不会被导航走。
     */
    handleOpenInstalledLink () {
        const {scheme} = this.linkSettings();
        this.launchedAt = Date.now();
        this.setState({launchAttempted: true});
        try {
            const frame = document.createElement('iframe');
            frame.style.display = 'none';
            frame.setAttribute('aria-hidden', 'true');
            frame.src = scheme;
            document.body.appendChild(frame);
            setTimeout(() => {
                if (frame.parentNode) frame.parentNode.removeChild(frame);
            }, 2000);
        } catch (e) {
            // Nothing else to try: the hint under the button tells the user how
            // to start it by hand.
        }
        // Starting the program takes a moment; give it a few chances to answer.
        this.setState({linkInstalled: null});
        this.detectKidsblocklinkInstalled();
    }

    handleBannerClose () {
        this.stopPolling();
        this.setState({bannerDismissed: true});
    }

    handleItemSelect (item) {
        const id = item.deviceId;
        const deviceType = item.type;
        const pnpidList = item.pnpidList;
        const deviceExtensions = item.deviceExtensions;

        if (id && !item.disabled) {
            if (this.props.vm.extensionManager.isDeviceLoaded(id)) {
                this.props.onDeviceSelected(id);
            } else {
                this.props.vm.extensionManager.loadDeviceURL(id, deviceType, pnpidList).then(() => {
                    this.props.vm.extensionManager.getDeviceExtensionsList().then(() => {
                        this.props.vm.installDeviceExtensions(deviceExtensions);
                    });
                    this.props.onDeviceSelected(id);
                    analytics.event({
                        category: 'devices',
                        action: 'select device',
                        label: id
                    });
                });
            }
        }
    }

    /**
     * 渲染 KidsBlock Link 提示弹窗：先检测，检测不到才提示安装，并提供
     * "打开已安装的 KidsBlock Link"。检测本身一直在后台跑，所以不需要
     * 手动"重新检测"的按钮。
     * @returns {React.Element|null} 未检测到 Link 时才返回弹窗
     */
    renderLinkBanner () {
        if (this.state.linkInstalled !== false || this.state.bannerDismissed) {
            return null;
        }
        const {intl} = this.props;
        const os = detectOS();
        const {downloadUrls} = this.linkSettings();
        // 当前系统的按钮高亮，另一个置灰；写成小函数免得 JSX 那一行超长。
        const downloadButtonClass = name => {
            const state = os === name ? styles.linkDownloadBtnActive : styles.linkDownloadBtnGray;
            return `${styles.linkDownloadBtn} ${state}`;
        };

        return (
            <div className={styles.linkOverlay}>
                <div className={styles.linkModal}>
                    {/* 标题栏 */}
                    <div className={styles.linkModalHeader}>
                        <span className={styles.linkModalHeaderIcon}>
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"
                                    stroke="#fff"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <path
                                    d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"
                                    stroke="#fff"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </span>
                        <span className={styles.linkModalHeaderTitle}>
                            {intl.formatMessage(messages.linkModalTitle)}
                        </span>
                        <button
                            className={styles.linkModalClose}
                            onClick={this.handleBannerClose}
                            title="Close"
                        >
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M18 6L6 18M6 6l12 12"
                                    stroke="#fff"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                />
                            </svg>
                        </button>
                    </div>

                    {/* 弹窗内容 */}
                    <div className={styles.linkModalBody}>
                        {/* 顶部说明 */}
                        <div className={styles.linkModalIntro}>
                            <span className={styles.linkModalIntroIcon}>
                                <svg
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <circle
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="#aaa"
                                        strokeWidth="1.8"
                                    />
                                    <path
                                        d="M12 8v4m0 4h.01"
                                        stroke="#aaa"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                    />
                                </svg>
                            </span>
                            <p className={styles.linkModalIntroText}>
                                {intl.formatMessage(messages.linkModalIntro)}
                            </p>
                        </div>

                        {/* 步骤 1 */}
                        <div className={styles.linkStep}>
                            <div className={styles.linkStepContent}>
                                <p className={styles.linkStepTitle}>
                                    {intl.formatMessage(messages.linkStep1Title)}
                                </p>
                                <div className={styles.linkDownloadRow}>
                                    {/* Windows */}
                                    <div className={styles.linkDownloadItem}>
                                        <div className={styles.linkOsIcon}>
                                            <img
                                                className={styles.linkOsIconImage}
                                                src={windowsIcon}
                                                alt=""
                                            />
                                        </div>
                                        <span className={styles.linkOsName}>{'Windows'}</span>
                                        <a
                                            className={downloadButtonClass('windows')}
                                            href={downloadUrls.windows}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {os === 'windows' ?
                                                intl.formatMessage(messages.linkDownloadWindowsActive) :
                                                intl.formatMessage(messages.linkDownloadWindows)
                                            }
                                        </a>
                                    </div>
                                    {/* Mac */}
                                    <div className={styles.linkDownloadItem}>
                                        <div className={styles.linkOsIcon}>
                                            <img
                                                className={styles.linkOsIconImage}
                                                src={macIcon}
                                                alt=""
                                            />
                                        </div>
                                        <span className={styles.linkOsName}>{'Mac'}</span>
                                        <a
                                            className={downloadButtonClass('mac')}
                                            href={downloadUrls.mac}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {os === 'mac' ?
                                                intl.formatMessage(messages.linkDownloadMacActive) :
                                                intl.formatMessage(messages.linkDownloadMac)
                                            }
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 步骤 2 */}
                        <div className={styles.linkStep}>
                            <div className={styles.linkStepContent}>
                                <p className={styles.linkStepTitle}>
                                    {intl.formatMessage(messages.linkStep2Title)}
                                </p>
                            </div>
                        </div>

                        {/* 已经装好了：直接打开本机上的 KidsBlock Link */}
                        <div className={styles.linkStep}>
                            <div className={styles.linkStepContent}>
                                <p className={styles.linkInstalledTitle}>
                                    {intl.formatMessage(messages.linkInstalledQuestion)}
                                </p>
                                <div className={styles.linkActionRow}>
                                    <button
                                        className={styles.linkOpenBtn}
                                        onClick={this.handleOpenInstalledLink}
                                    >
                                        {intl.formatMessage(messages.linkOpenInstalled)}
                                    </button>
                                </div>
                                {/* 平时不占地方：「没检测到」下面那句已经说明了怎么做，
                                    只有点过按钮或浏览器拦截时才有必要多说一句。 */}
                                {this.state.launchAttempted || this.state.localNetworkBlocked ?
                                    <p className={styles.linkStatus}>
                                        {this.state.launchAttempted ?
                                            intl.formatMessage(messages.linkLaunchAttempted) :
                                            intl.formatMessage(messages.linkBlockedByBrowser)
                                        }
                                    </p> :
                                    null
                                }
                                <p className={styles.linkHint}>
                                    {intl.formatMessage(messages.linkLaunchHint)}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    render () {
        const deviceLibraryThumbnailData = this.props.deviceData.map(device => ({
            rawURL: device.iconURL || deviceIcon,
            ...device
        }));

        const overlay = this.renderLinkBanner();

        return (
            <LibraryComponent
                data={deviceLibraryThumbnailData}
                filterable
                tags={tagListPrefix}
                id="deviceLibrary"
                title={this.props.intl.formatMessage(messages.deviceTitle)}
                visible={this.props.visible}
                overlayContent={overlay}
                onItemSelected={this.handleItemSelect}
                onRequestClose={this.props.onRequestClose}
            />
        );
    }
}

DeviceLibrary.propTypes = {
    deviceData: PropTypes.instanceOf(Array).isRequired,
    intl: intlShape.isRequired,
    onDeviceSelected: PropTypes.func,
    onRequestClose: PropTypes.func,
    onSetDeviceData: PropTypes.func.isRequired,
    visible: PropTypes.bool,
    vm: PropTypes.instanceOf(VM).isRequired // eslint-disable-line react/no-unused-prop-types
};

const mapStateToProps = state => ({
    deviceData: state.scratchGui.deviceData.deviceData
});

const mapDispatchToProps = dispatch => ({
    onSetDeviceData: data => dispatch(setDeviceData(data))
});

export default compose(
    injectIntl,
    connect(
        mapStateToProps,
        mapDispatchToProps
    )
)(DeviceLibrary);
